<template>
    <section
        :class="[
            'email-banner-container',
            { 'email-banner-container--sidebar': sidebar}
        ]"
        :style="style"
    >
        <div
            v-show="currentStep === 1"
            class="email-banner"
        >
            <div
                v-if="showTop"
                class="email-banner__header-top"
                :class="{ 'email-banner__header-top--single': columns === '1'}"
            >
                <h3
                    v-if="topHeader"
                    class="email-banner__top-heading"
                >
                    {{ topHeader }}
                </h3>
                <p
                    v-if="topSubHeader"
                    class="email-banner__top-subtext"
                >
                    {{ topSubHeader }}
                </p>
                <img
                    v-if="topImage"
                    :src="topImage.url"
                    class="email-banner__header-top-image"
                    :class="{ 'email-banner__header-top-image--single': columns === '1'}"
                >
                <p
                    v-if="topBottomText"
                    class="email-banner__top-bottom"
                >
                    {{ topBottomText }}
                </p>
            </div>

            <div v-if="showTop" class="email-banner__divider"></div>

            <div class="email-banner__form">
                <div
                    v-if="header || subtext"
                    class="email-banner__headers"
                >
                    <h4
                        v-if="header"
                        class="email-banner__form-header"
                    >
                        {{ header }}
                    </h4>
                    <p
                        v-if="subtext"
                        class="email-banner__form-sub-header"
                    >
                        {{ subtext }}
                    </p>
                </div>
                <form
                    class="email-banner__form-inner"
                    :class="{ 'email-banner__form-inner--single': columns === '1'}"
                    novalidate
                    @submit.prevent
                >
                    <!-- Hidden fields for AC List and Tag IDs -->
                    <ds-atom-input
                        v-for="input in inputs"
                        :id="input.systemkey"
                        :key="input.systemkey"
                        :name="input.systemkey"
                        type="text"
                        :label="input.label"
                        :placeholder="input.placeholder"
                        :mask="input.mask"
                        :validation="input.validation"
                        :value="fields[input.systemkey].value"
                        :is-valid="fields[input.systemkey].valid"
                        :error="input.errorMessage"
                        :icon="input.icon"
                        :prefix="input.prefix"
                        :suffix="input.suffix"
                        @update-value="value => fields[input.systemkey].value = value"
                        @update-validation="state => setField({ id: input.systemkey, state })"
                    />

                    <div class="email-banner__button-wrapper">
                        <div
                            v-if="button"
                            class="email-banner__form-button buttons-orange"
                            @click="submit"
                        >
                            <span
                                class="email-banner__button-label link"
                            >
                                {{ button }}
                            </span>
                        </div>
                    </div>
                </form>
                <p
                    v-if="invalidLabel && sendError"
                    class="email-banner__error"
                >
                    {{ invalidLabel }}
                </p>
            </div>
        </div>

        <div
            v-show="currentStep === 2"
            class="email-banner-thank-you"
        >
            <h2
                v-if="thankYouHeader"
                class="email-banner-thank-you-header"
            >
                {{ thankYouHeader }}
            </h2>
            <p
                v-if="thankYouText"
                class="email-banner-thank-you-text"
            >
                {{ thankYouText }}
            </p>
        </div>
    </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import axios from 'axios';
import { DsAtomInput } from '@leads.io/design-system-component-library/marketplaces';

const props = defineProps({
    inputs: {
        type: Array || Boolean,
        required: false,
        default: () => [],
    },
    columns: {
        type: String,
        required: false,
        default: '2',
    },
    header: {
        type: String,
        required: false,
        default: null,
    },
    subtext: {
        type: String,
        required: false,
        default: null,
    },
    invalidLabel: {
        type: String,
        required: false,
        default: null,
    },
    button: {
        type: String,
        required: false,
        default: null,
    },
    showTop: {
        type: Boolean,
        required: false,
        default: false,
    },
    sidebar: {
        type: Boolean,
        required: false,
        default: false,
    },
    topHeader: {
        type: String,
        required: false,
        default: null,
    },
    topSubHeader: {
        type: String,
        required: false,
        default: null,
    },
    topImage: {
        type: [Object, Boolean],
        required: false,
        default: null,
    },
    topBottomText: {
        type: String,
        required: false,
        default: null,
    },
    thankYouHeader: {
        type: String,
        required: false,
        default: null,
    },
    thankYouText: {
        type: String,
        required: false,
        default: null,
    },
    bannerBackgroundColor: {
        type: String,
        required: false,
        default: null,
    },
    topHeaderColor: {
        type: String,
        required: false,
        default: null,
    },
    topSubtextColor: {
        type: String,
        required: false,
        default: null,
    },
    topBottomTextColor: {
        type: String,
        required: false,
        default: null,
    },
    dividerColor: {
        type: String,
        required: false,
        default: null,
    },
    headerColor: {
        type: String,
        required: false,
        default: null,
    },
    subtextColor: {
        type: String,
        required: false,
        default: null,
    },
    labelColor: {
        type: String,
        required: false,
        default: null,
    },
    inputBorderColor: {
        type: String,
        required: false,
        default: null,
    },
    inputBorderInvalidColor: {
        type: String,
        required: false,
        default: null,
    },
    thankYouHeaderColor: {
        type: String,
        required: false,
        default: null,
    },
    thankYouTextColor: {
        type: String,
        required: false,
        default: null,
    },
    listId: {
        type: [String, Number, null],
        required: false,
        default: null,
    },
    tagId: {
        type: [String, Number, null],
        required: false,
        default: null,
    },
});

const currentStep = ref(1);
const fields = ref({});
const sendError = ref(false);

const addFields = () => {
    props.inputs.forEach(item => {
        fields.value[item.systemkey] = {
            value: '',
            valid: null,
        };
    });
};

addFields();

const setField = payload => {
    fields.value[payload['id']].valid = payload['state'];
};

const submit = () => {
    let submit = true;
    sendError.value = false;

    Object.entries(fields.value).forEach(item => {
        if (item[1].valid === false || item[1].valid === null) {
            item[1].valid = false;
            submit = false;
        }
    });

    if (submit === true) {
        submitData();
    }
};

const submitData = () => {
    const data = {
        hm_subscribe: true,
        list: props.listId,
        tag: props.tagId,
    };

    for (const [key, field] of Object.entries(fields.value)) {
        data[key] = field.value;
    }
    axios.post('api/email-banner', data)
        .then(() => {
            console.log('Email banner submitted successfully', data);
            currentStep.value = 2;
        })
        .catch(error => {
            sendError.value = true;
            console.log(error.response.data);
        });
};

const style = computed(() => ({
    '--background-color': props.bannerBackgroundColor || 'initial',
    '--top-header-color': props.topHeaderColor || 'initial',
    '--top-subtext-color': props.topSubtextColor || 'initial',
    '--top-bottom-color': props.topBottomTextColor || 'initial',
    '--divider-color': props.dividerColor || 'initial',
    '--header-color': props.headerColor || 'initial',
    '--subtext-color': props.subtextColor || 'initial',
    '--label-color': props.labelColor || 'initial',
    '--input-border-color': props.inputBorderColor || 'initial',
    '--input-border-invalid-color': props.inputBorderInvalidColor || 'initial',
    '--thankyou-header-color': props.thankYouHeaderColor || 'initial',
    '--thankyou-text-color': props.thankYouTextColor || 'initial',
}));
</script>

<style lang="scss">
@use 'watson-theme-email-banner';
@use 'form-styling/input';
</style>
